import { useEffect, useMemo, useState } from 'react';
import { HomeNavButton } from '../../components/NavButtons';
import { getSchoolPeriods, getWeeklySchedule } from '../../services/calendarService';
import {
  WEEK_DAYS,
  countScheduleItems,
  countTodayItems,
  formatDuration,
  formatPeriodRange,
  formatTime,
  getDayLabel,
  getTodayDayOfWeek,
  groupScheduleByDay,
  resolvePeriods,
} from '../../utils/calendarUtils';
import {
  getClassTitle,
  getSubjectTitle,
  getTeacherFromItem,
  getTeacherName,
} from '../../utils/classSubjectUtils';

const eventThemes = [
  { background: 'linear-gradient(135deg, #EB8181 0%, #E02727 100%)', accent: '#E02727' },
  { background: 'linear-gradient(135deg, #FFB55A 0%, #FF9238 100%)', accent: '#FF9238' },
  { background: 'linear-gradient(135deg, #75E3F7 0%, #00C7EA 100%)', accent: '#00A8A2' },
  { background: 'linear-gradient(135deg, #B9ABFF 0%, #896CFF 100%)', accent: '#896CFF' },
  { background: 'linear-gradient(135deg, #ABCEF5 0%, #67A8EF 100%)', accent: '#54B0F8' },
  { background: 'linear-gradient(135deg, #7FDE86 0%, #1FC24E 100%)', accent: '#1FC24E' },
];

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M7 12.25C9.8995 12.25 12.25 9.8995 12.25 7C12.25 4.1005 9.8995 1.75 7 1.75C4.1005 1.75 1.75 4.1005 1.75 7C1.75 9.8995 4.1005 12.25 7 12.25Z" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4.375V7L8.75 8.75" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ScheduleEventCard({ item, index }) {
  const theme = eventThemes[index % eventThemes.length];
  const period = item.period;
  const startTime = period?.startTime || item.startTime;
  const endTime = period?.endTime || item.endTime;

  return (
    <article
      className="relative overflow-hidden rounded-[10px] p-3 text-white shadow-sm"
      style={{ background: theme.background, boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.08)' }}
    >
      <div className="absolute -left-6 -top-6 h-20 w-20 rounded-full bg-white/10" />
      <div className="absolute -bottom-8 -right-4 h-24 w-24 rounded-full bg-white/10" />

      <div className="relative flex items-start gap-3">
        <div className="flex shrink-0 flex-col items-center rounded-[8px] bg-white/95 px-2.5 py-2 text-center">
          <p className="text-[12px] font-bold leading-none" style={{ color: theme.accent }}>
            {formatTime(startTime?.slice?.(0, 5) || startTime)}
          </p>
          <span className="my-1 h-px w-full bg-slate-200" />
          <p className="text-[10px] font-light text-slate-500">
            {formatTime(endTime?.slice?.(0, 5) || endTime)}
          </p>
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-bold">
            {getSubjectTitle(item) || item.title || 'بدون عنوان'}
          </p>
          {period?.title && (
            <p className="mt-0.5 text-[10px] font-bold text-white/90">{period.title}</p>
          )}
          <p className="mt-1 text-[11px] font-light text-white/90">
            {getClassTitle(item)} | {getTeacherName(getTeacherFromItem(item))}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-[5px] bg-white/20 px-2 py-1 text-[10px]">
              <ClockIcon />
              {formatDuration(startTime, endTime) || formatPeriodRange(period) || 'زمان نامشخص'}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

function DayScheduleList({ dayItems, emptyMessage }) {
  if (dayItems.length === 0) {
    return (
      <div className="rounded-[10px] bg-white px-4 py-8 text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
          <img className="h-8 w-8 opacity-60" src="/images/calender.webp" alt="" />
        </div>
        <p className="text-[13px] font-bold text-slate-800">برنامه‌ای ثبت نشده</p>
        <p className="mt-2 text-[11px] text-slate-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {dayItems.map((item, index) => (
        <ScheduleEventCard key={item.id} item={item} index={index} />
      ))}
    </div>
  );
}

function CalendarPage() {
  const [scheduleItems, setScheduleItems] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedDay, setSelectedDay] = useState(getTodayDayOfWeek());

  const todayIndex = getTodayDayOfWeek();
  const groupedDays = useMemo(() => groupScheduleByDay(scheduleItems), [scheduleItems]);
  const periodRows = useMemo(
    () => resolvePeriods(periods, scheduleItems),
    [periods, scheduleItems],
  );
  const selectedDayData = groupedDays.find((day) => day.index === selectedDay) || { items: [] };
  const totalCount = countScheduleItems(scheduleItems);
  const todayCount = countTodayItems(scheduleItems, todayIndex);

  useEffect(() => {
    let isMounted = true;

    async function loadSchedule() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const [items, schoolPeriods] = await Promise.all([
          getWeeklySchedule(),
          getSchoolPeriods(),
        ]);

        if (isMounted) {
          setScheduleItems(items);
          setPeriods(schoolPeriods);
        }
      } catch (error) {
        if (isMounted) {
          setScheduleItems([]);
          setErrorMessage(error.message || 'امکان دریافت برنامه هفتگی وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSchedule();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p
            style={{ background: 'linear-gradient(90deg, #EB8181 0%, #E02727 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
            className="rounded-[10px] px-6 py-1 text-[15px] text-white"
          >
            برنامه هفتگی
          </p>

          <img className="h-[50px]" src="/images/calender.webp" alt="" />
        </div>

        <HomeNavButton />
      </div>

      <div>
        <p className="text-[11px] font-light">برنامه کلاسی هفتگی شما</p>
      </div>

      <div
        className="grid grid-cols-2 gap-2 rounded-[8px] p-2"
        style={{ background: 'rgba(217, 217, 217, 0.3)' }}
      >
        <div className="rounded-[6px] px-3 py-2 text-white" style={{ background: 'linear-gradient(90deg, #EB8181 0%, #E02727 100%)' }}>
          <p className="text-[11px] font-light">کل برنامه‌های هفته</p>
          <p className="mt-1 text-[17px] font-bold">{totalCount}</p>
        </div>

        <div className="rounded-[6px] px-3 py-2 text-white" style={{ background: 'rgba(0, 192, 185, 0.54)' }}>
          <p className="text-[11px] font-light">برنامه‌های امروز</p>
          <p className="mt-1 text-[17px] font-bold">{todayCount}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-[10px] bg-white px-4 py-10 text-center text-[12px] text-slate-500">
          در حال دریافت برنامه هفتگی...
        </div>
      ) : errorMessage ? (
        <div className="rounded-[10px] bg-white px-4 py-5 text-center">
          <p className="text-[12px] font-bold text-[#E02727]">{errorMessage}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 rounded-[6px] bg-[#00C0B9] px-5 py-1 text-[12px] font-bold text-white"
          >
            تلاش دوباره
          </button>
        </div>
      ) : totalCount === 0 ? (
        <div className="rounded-[10px] bg-white px-4 py-8 text-center">
          <p className="text-[13px] font-bold text-slate-800">هنوز برنامه‌ای برای این هفته ثبت نشده</p>
          <p className="mt-2 text-[11px] text-slate-500">
            وقتی برنامه آموزشی جدید اضافه شود، اینجا نمایش داده می‌شود.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
            <div className="-mx-1 overflow-x-auto px-1 pb-1">
              <div className="flex min-w-max gap-2">
                {WEEK_DAYS.map((day) => {
                  const dayItems = groupedDays.find((groupedDay) => groupedDay.index === day.index)?.items || [];
                  const isSelected = selectedDay === day.index;
                  const isToday = todayIndex === day.index;

                  return (
                    <button
                      key={day.index}
                      type="button"
                      onClick={() => setSelectedDay(day.index)}
                      className={`flex min-w-[72px] flex-col items-center rounded-[10px] px-3 py-2 transition-all ${
                        isSelected
                          ? 'bg-[#E02727] text-white shadow-md'
                          : 'bg-white text-slate-700'
                      }`}
                      style={!isSelected ? { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' } : undefined}
                    >
                      <span className="text-[11px] font-bold">{day.short}</span>
                      <span className={`mt-1 text-[9px] ${isSelected ? 'text-white/90' : 'text-slate-500'}`}>
                        {dayItems.length} برنامه
                      </span>
                      {isToday && (
                        <span className={`mt-1 rounded-full px-2 py-0.5 text-[8px] ${isSelected ? 'bg-white/20' : 'bg-[#00C0B9]/15 text-[#00A8A2]'}`}>
                          امروز
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[10px] bg-white/70 p-3" style={{ background: 'rgba(217, 217, 217, 0.25)' }}>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-[13px] font-bold text-slate-800">
                  {getDayLabel(selectedDay)}
                  {selectedDay === todayIndex && (
                    <span className="mr-2 rounded-[4px] bg-[#00C0B9]/15 px-2 py-0.5 text-[10px] text-[#00A8A2]">
                      امروز
                    </span>
                  )}
                </p>
                <p className="text-[10px] text-slate-500">{selectedDayData.items.length} برنامه</p>
              </div>

              {periodRows.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {periodRows.map((period) => {
                    const slotItems = selectedDayData.items.filter((item) => item.periodId === period.id);

                    return (
                      <div key={period.id}>
                        <p className="mb-1 text-[11px] font-bold text-slate-600">
                          {period.title}
                          {formatPeriodRange(period) ? ` (${formatPeriodRange(period)})` : ''}
                        </p>
                        <DayScheduleList
                          dayItems={slotItems}
                          emptyMessage="برای این زنگ برنامه‌ای نیست."
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <DayScheduleList
                  dayItems={selectedDayData.items}
                  emptyMessage={`برای ${getDayLabel(selectedDay)} هنوز برنامه‌ای ثبت نشده است.`}
                />
              )}
            </div>
          </div>

          <div className="hidden overflow-x-auto rounded-[12px] bg-white md:block">
            <table className="w-full min-w-[760px] border-collapse text-right">
              <thead>
                <tr>
                  <th className="border border-slate-100 bg-slate-50 px-3 py-2 text-[12px] font-bold text-slate-500">
                    زنگ
                  </th>
                  {WEEK_DAYS.map((day) => (
                    <th
                      key={day.index}
                      className={`border border-slate-100 px-2 py-2 text-[12px] font-bold ${
                        todayIndex === day.index ? 'bg-[#00C0B9]/10 text-[#00A8A2]' : 'bg-slate-50 text-slate-800'
                      }`}
                    >
                      {day.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periodRows.map((period) => (
                  <tr key={period.id}>
                    <td className="border border-slate-100 bg-slate-50 px-3 py-2 align-top">
                      <p className="text-[12px] font-bold text-slate-800">{period.title}</p>
                    </td>
                    {WEEK_DAYS.map((day) => {
                      const slotItems = (groupedDays.find((groupedDay) => groupedDay.index === day.index)?.items || [])
                        .filter((item) => item.periodId === period.id);

                      return (
                        <td key={`${period.id}-${day.index}`} className="border border-slate-100 p-2 align-top">
                          {slotItems.length === 0 ? (
                            <span className="text-[12px] text-slate-300">—</span>
                          ) : (
                            slotItems.map((item) => (
                              <div key={item.id} className="mb-1 last:mb-0">
                                <p className="text-[12px] font-bold text-slate-800">
                                  {getSubjectTitle(item) || item.title || 'بدون عنوان'}
                                </p>
                                <p className="text-[10px] text-slate-500">
                                  {getClassTitle(item)} | {getTeacherName(getTeacherFromItem(item))}
                                </p>
                              </div>
                            ))
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </>
      )}

      <div className="mx-auto flex w-full flex-col items-center justify-center gap-2">
        <p className="text-center text-[11px] font-light text-slate-600">
          روز مورد نظرت را انتخاب کن و برنامه‌های آموزشی‌ات را ببین.
        </p>

        {todayCount > 0 && (
          <button
            type="button"
            onClick={() => setSelectedDay(todayIndex)}
            className="mx-auto flex w-11/12 items-center justify-center rounded-[5px] border-b-[2px] border-b-[#299A96] bg-[#00C0B9] py-1.5 text-center text-[13px] font-bold text-white"
          >
            مشاهده برنامه امروز
          </button>
        )}
      </div>
    </div>
  );
}

export default CalendarPage;
